import React, { useEffect, useMemo, useState } from "react";
import * as S from "./FollowUpView.style";
import { useTranslation } from "react-i18next";
import FollowUpBox from "../followUpBox/FollowUpBox";
import { useQuery, useSubscription } from "@apollo/client";
import { useStateValue } from "../../../../../../../contextAPI/StateProvider";
import { SUBSCRIPTION_GET_USER_TO_PROCESS_BY_ISACEPTED } from "../../../../../../../graphql/subscriptions/UserToProcess";
import ZaiaReaction from "../../../../../../zaiaComponents/zaiaReaction";
import useRecordNavigation from "../../../../../../../hooks/useRecordNavigation";
import { Actions } from "../../../../../../../utils/actionsIds";
import useAssignSelectPatient from "../../../../../../../hooks/useAssignSelectPatient";
import Popup from "reactjs-popup";
import FlorenzSelect from "../../../../../../florenzComponents/florenzSelect";
import { QUERY_GET_TYPE_MEDICINE_STAGE } from "../../../../../../../graphql/queries/Process";
import * as P from "../../../../../../../utils/pipes";
import { QUERY_GET_USER_TO_PROCESS_BY_ISACEPTED, QUERY_GET_USER_TO_PROCESS_COUNT_FILTERED } from "../../../../../../../graphql/queries/UserToProcess";


const logoZaiaEmpty = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/no-results.svg`;
const logoZaiaLoading = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;
const logoZaiaAlert = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-error-data.svg`;

const ENV = process.env.REACT_APP_ENV;

function FollowUpView({
  inputText,
  textInput,
  setInputText
}) {

  const {t, i18n: { language }} = useTranslation("global");
  const [{ followUpActive, followUpMinimized, followUpNeedsUpdate }, dispatch] = useStateValue();
  const { recordAction } = useRecordNavigation();
  const { selectPatient } = useAssignSelectPatient();
  
  const loadLimit = 20; 
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [offset, setOffset] = useState(loadLimit);

  const [usersList, setUsersList] = useState([]);

  const [filterStageTypeStatus, setFilterStageTypeStatus] = useState([]);

  const stages = [
    {
      id: 0,
      key: "all",
      name: t("globally.all"),
      color: "#3E3DA3"
    },
    {
      id: 1,
      key: "due",
      name: t("followUp.due"),
      color: "#FF8181",
      hoverColor: "#F15050",
      backgroundColor: "#FFF8F8"
    },
    {
      id: 2,
      key: "today",
      name: t("globally.today"),
      color: "#4DB4FF",
      hoverColor: "#0F9AFF",
      backgroundColor: "#E7F5FF"
    },
    {
      id: 3,
      key: "next",
      name: t("followUp.next"),
      color: "#2ED078",
      hoverColor: "#019947",
      backgroundColor: "#E9F9F0"
    }
  ]

  const [selectedState, setSelectedState ] = useState(stages[0]); 

  const { data: groupStageTypeData, loading: groupStageTypeLoading, error: groupStageTypeError } = useQuery(QUERY_GET_TYPE_MEDICINE_STAGE);


  const {
    where,
    whereCount,
    whereDueCombined,
    whereTodayCombined,
    whereNextCombined
  } = useMemo(() => {
    const w = 
      ENV === "production"
      ? {
        isAcepted: { _eq: true },
        stageId: { _nin: [
          "enroll", 
          "suspension_esquema_progresion",
          "completo_ciclos_tratamiento_programados", 
          "ReferredToAnotherInstitution", 
          "deceased", 
          "abandono_tratamiento", 
          "tres_contactos_telefonicos_fallidos", 
          "no_continua_seguimiento_decision_paciente",
          "suspension_tratamiento_cambio_esquema",
          "suspension_esquema_toxicidad",
          "Motivos_Otros"
        ] }
      }
      : {
        isAcepted: { _eq: true },
        stageId: { _nin: ["enroll"] }
      };

    const now = new Date();

    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);

    const whereDue = {
      nextUpdate: {
        _lt: startOfToday.toISOString()
      }
    };

    const whereToday = {
      nextUpdate: {
        _gte: startOfToday.toISOString(),
        _lte: endOfToday.toISOString()
      }
    };

    const whereNext = {
      nextUpdate: {
        _gt: endOfToday.toISOString()
      }
    };

    if (selectedState.key === "next") {
      w.nextUpdate = whereNext.nextUpdate;
    }

    if (selectedState.key === "today") {
      w.nextUpdate = whereToday.nextUpdate;
    }

    if (selectedState.key === "due") {
      w.nextUpdate = whereDue.nextUpdate;
    }

    if (inputText && inputText.trim() !== "") {
      const terms = inputText.trim().split(/\s+/);
      w._and = terms.map(term => ({
        _or: [
          { user: { name: { _ilike: `%${term}%` } } },
          { user: { lastname: { _ilike: `%${term}%` } } },
          { user: { email: { _ilike: `%${term}%` } } },
          { user: { documentNumber: { _ilike: `%${term}%` } } },
        ]
      }));
    }

    if (filterStageTypeStatus.length > 0 && filterStageTypeStatus.length !== (groupStageTypeData?.enTypeMedicineStage?.length || 0) + 1) {
      const normalValues = filterStageTypeStatus.filter(v => v !== "others");
      const orConditions = [];

      if (normalValues.length > 0) {
        orConditions.push(
          ...normalValues.map(value => ({
            processStage: {
              groupStage: {
                groupStageTypes: {
                  typeMedicineStageId: { _eq: value }
                }
              }
            }
          }))
        );
      }

      if (filterStageTypeStatus.includes("others")) {
        orConditions.push(
          {
            processStage: {
              groupStageValue: { _is_null: true }
            }
          },
          {
            processStage: {
              groupStage: {
                groupStageTypes_aggregate: {
                  count: {
                    predicate: {
                      _eq: 0
                    }
                  }
                }
              }
            }
          }
        );
      }

      w._and = w._and || [];
      w._and.push({
        _or: orConditions
      });
    }

    const whereCount = Object.fromEntries(
      Object.entries(w).filter(([key]) => key !== "nextUpdate")
    );

    const whereDueCombined = {
      ...w,
      ...whereDue
    };

    const whereTodayCombined = {
      ...w,
      ...whereToday
    };

    const whereNextCombined = {
      ...w,
      ...whereNext
    };

    return {
      where: w,
      whereCount,
      whereDueCombined,
      whereTodayCombined,
      whereNextCombined
    };
  }, [selectedState.key, inputText, filterStageTypeStatus, groupStageTypeData?.enTypeMedicineStage]);

  const orderBy = useMemo(() => {
    const order = [];  
  
    // fallback importante
    if (order.length === 0) {
      order.push({ nextUpdate: "asc" });
    }
  
    return order;
  }, []);

  const { data: userToProcesses, loading: userToProcessesLoading, error: userToProcessesError, fetchMore, refetch } = useQuery(
    QUERY_GET_USER_TO_PROCESS_BY_ISACEPTED, 
    { 
      variables: { 
        where,
        limit: loadLimit, 
        offset: 0, 
        orderBy
      },
      fetchPolicy: "network-only"
    },
  );    

  const { data: userToProcessesCountFiltered, refetch: refetchCount } = useQuery(
    QUERY_GET_USER_TO_PROCESS_COUNT_FILTERED, 
    { 
      variables: { 
        where: whereCount
      },
      fetchPolicy: "network-only"
    },
  );  
  
  const {data: dueQuery, refetch: refetchDue} = useQuery(QUERY_GET_USER_TO_PROCESS_COUNT_FILTERED, {
    variables: { where: whereDueCombined },
    fetchPolicy: "network-only"
  });

  const {data: todayQuery, refetch: refetchToday} = useQuery(QUERY_GET_USER_TO_PROCESS_COUNT_FILTERED, {
    variables: { where: whereTodayCombined },
    fetchPolicy: "network-only"
  });

  const {data: nextQuery, refetch: refetchNext} = useQuery(QUERY_GET_USER_TO_PROCESS_COUNT_FILTERED, {
    variables: { where: whereNextCombined },
    fetchPolicy: "network-only"
  });

  useEffect(() => {    
    if (refetch) refetch();
    if (refetchCount) refetchCount();
    if (refetchDue) refetchDue();
    if (refetchToday) refetchToday();
    if (refetchNext) refetchNext();
  }, [followUpActive, followUpMinimized, refetch, refetchCount, refetchDue, refetchToday, refetchNext]);

  useEffect(() => {
    if (followUpNeedsUpdate) {
      if (refetch) refetch();
      if (refetchCount) refetchCount();
      if (refetchDue) refetchDue();
      if (refetchToday) refetchToday();
      if (refetchNext) refetchNext();

      dispatch({
        type: "SET_FOLLOWUP_NEEDS_UPDATE",
        followUpNeedsUpdate: false,
      });
    }
  }, [followUpNeedsUpdate, refetch, refetchCount, refetchDue, refetchToday, refetchNext, dispatch]);

  useEffect(() => {
    if (userToProcessesLoading) return;
    if (userToProcesses?.userToProcess) {
      const users = userToProcesses.userToProcess;
      setUsersList(users);
      setOffset(users.length);
      
      if (users.length <= loadLimit) {
        setHasMore(users.length === loadLimit);
      }
    }
  }, [userToProcessesLoading, userToProcesses]);


  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !userToProcessesLoading && !isFetchingMore) {
      setIsFetchingMore(true);
      fetchMore({
        variables: {
          limit: loadLimit,
          offset: offset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult || fetchMoreResult.userToProcess.length === 0) {
            setHasMore(false);
            return prev;
          }

          const existingIds = new Set(prev.userToProcess.map(p => p.id));
          const newItems = fetchMoreResult.userToProcess.filter(p => !existingIds.has(p.id));

          setUsersList((prevList) => {
            const listIds = new Set(prevList.map(p => p.id));
            const filteredNew = newItems.filter(p => !listIds.has(p.id));
            return [...prevList, ...filteredNew];
          });

          setOffset((prevOffset) => prevOffset + newItems.length);

          if (fetchMoreResult.userToProcess.length < loadLimit) {
            setHasMore(false);
          }

          return {
            ...prev,
            userToProcess: [...prev.userToProcess, ...newItems]
          };
        }
      }).then(({ data }) => {
        if (!data || !data.userToProcess || data.userToProcess.length < loadLimit) {
          setHasMore(false);
        }
        setIsFetchingMore(false);
      }).catch(() => {
        setIsFetchingMore(false);
      });
    }
  };

  useEffect(() => {

    const stageTypes = groupStageTypeData ? groupStageTypeData?.enTypeMedicineStage.map((type) => type.value) : [];

    setFilterStageTypeStatus([...stageTypes, "others"]);
  }, [groupStageTypeData])

  const setFollowUpActive = (userToProcess) => {
    dispatch({
      type: "SET_FOLLOWUP_ACTIVE",
      followUpActive: userToProcess,
    });
    dispatch({
      type: "SET_FOLLOWUP_MINIMIZED",
      followUpMinimized: false,
    });
  }
  
  const currentStage = (nextUpdate) => {    
    const nextUpdateDate = nextUpdate ? new Date(nextUpdate) : new Date(Date.now());
    if(
      nextUpdateDate.getDate() === new Date(Date.now()).getDate() &&
      nextUpdateDate.getMonth() === new Date(Date.now()).getMonth() &&
      nextUpdateDate.getFullYear() === new Date(Date.now()).getFullYear()
    ) {
      return stages[2]
    } else if(
      nextUpdateDate > new Date(Date.now())
    ) {
      return stages[3]
    } else {
      return stages[1]
    }
  }

  const selectFollowUpPatient = (userToProcess) => {    
    recordAction(Actions.AINAVIGATOR.SELECT_FOLLOW_UP_PATIENT);
    setFollowUpActive(userToProcess);
    selectPatient(userToProcess.user.id)
  }


  const removeAllStatesType = () => {
    setFilterStageTypeStatus([]);
  }

  const selectAllStatesType = () => {
    const stageTypes = groupStageTypeData ? groupStageTypeData?.enTypeMedicineStage.map((type) => type.value) : [];

    setFilterStageTypeStatus([...stageTypes, "others"]);
  }

  const addMedicineTypeItem = (value) => {
    if(filterStageTypeStatus.includes(value)) {
      setFilterStageTypeStatus(filterStageTypeStatus.filter(item => item !== value));
    } else {
      setFilterStageTypeStatus([...filterStageTypeStatus, value]);
    }
  }  
  
  return (
    <S.FollowUpView>
      <div className="header">
        <div className="filter-state">
          {
            stages.map((state, index) => {
              return (
                <div
                  key={state.id}
                  onClick={() => setSelectedState(state)}
                >
                  <S.TabItem
                    activeTab={selectedState.id === state.id}
                    key={index}
                    color={state.color}
                  > 
                    <div className="item-text">
                      {
                        `
                          ${state.name}
                          ${
                            index === 0 
                            ? userToProcessesCountFiltered?.userToProcess_aggregate.aggregate.count || 0
                            : index === 1
                            ? dueQuery?.userToProcess_aggregate.aggregate.count || 0
                            : index === 2
                              ? todayQuery?.userToProcess_aggregate.aggregate.count || 0
                              : nextQuery?.userToProcess_aggregate.aggregate.count || 0
                          }
                        `
                      }
                    </div>
                    { 
                      state.id !== 0 &&
                      <div className="item-point"></div>
                    }
                  </S.TabItem>
                </div>
              )
            })
          }
        </div>
        <div className="filter-types">
          {
            <Popup
              trigger={
                <div>
                  <FlorenzSelect
                    placeholder={t("followUp.allStages")}
                    label={t("globally.stageTypes")}
                    xsSize={true}
                    forWhiteBackgroud={true}
                    value={filterStageTypeStatus}
                    selectionText={
                      filterStageTypeStatus?.length > 0 
                      ? filterStageTypeStatus?.length + " " + 
                        (
                          filterStageTypeStatus?.length === 1
                            ? t("globally.stageSelected")
                            : t("globally.stagesSelected")
                        )
                      : t("followUp.allStages")
                    }
                  />                        
                </div>
              }
              closeOnDocumentClick
              keepTooltipInside
              position={["bottom center"]}
              nested
            >
              {
                close => (
                  <S.StatusSelector>
                    <div className="header">
                      {/* {t("followUp.programStages")} */}
                      {t("globally.stageTypes")}
                    </div>
                    <div className="list-groups">
                      {
                        <div className="program-group">
                          <div className="group-header">
                            <div className="group-header__select"
                              onClick={() => {
                                if(filterStageTypeStatus?.length === groupStageTypeData?.enTypeMedicineStage.length + 1) {
                                  removeAllStatesType();
                                } else {
                                  selectAllStatesType();
                                }
                              }}
                            >
                              {
                                filterStageTypeStatus?.length === groupStageTypeData?.enTypeMedicineStage.length + 1
                                ? t("aiNavigator.outcomes.clearSelection")
                                : t("aiNavigator.outcomes.selectAll")	
                              }
                            </div>
                          </div>
                          <div className="group-stages-list">
                            {
                              groupStageTypeData?.enTypeMedicineStage.map((type) => {
                                return (
                                  <div className="group-stage"
                                    key={type.value}
                                    onClick={() => {
                                      addMedicineTypeItem(type.value);
                                      // recordAction(Actions.AINAVIGATOR.FILTER_PATIENT_PROGRAM_STATUS, null, filterProgram);                                                
                                    }}
                                  >
                                    <i className={`group-stage__check icon ${filterStageTypeStatus?.includes(type.value) ?  "zaia-i-check-box" : "zaia-i-check-box-outline-blank"}`}/>
                                    <div className="group-stage__title">
                                      {P.dataTranslation(type.comment, language)}
                                    </div>
                                  </div>
                                )
                              })
                            }
                            <div className="group-stage"
                              key={"others"}
                              onClick={() => {
                                addMedicineTypeItem("others");
                                // recordAction(Actions.AINAVIGATOR.FILTER_PATIENT_PROGRAM_STATUS, null, filterProgram);                                                
                              }}
                            >
                              <i className={`group-stage__check icon ${filterStageTypeStatus?.includes("others") ?  "zaia-i-check-box" : "zaia-i-check-box-outline-blank"}`}/>
                              <div className="group-stage__title">
                                {t("globally.others")}
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </S.StatusSelector>
                )
              } 
            </Popup>     
          }
        </div>
      </div>

      <S.ButtonsContainer>
        <div className="buttons-container__caption">
          {
            inputText && (
            <div className="caption">
                <div className="caption__text">
                  {t("globally.resultsFor")}
                  <div>
                    <i
                      className="icon zaia-icono-cerrar"
                      onClick={() => {
                        setInputText("")
                        textInput.set = ""
                      }}
                    />
                    {inputText}
                  </div>
                </div>
              </div>
            )
          }
        </div>
      </S.ButtonsContainer>

      <div className="follow-up-list">
        <div className="follow-up-list__center" onScroll={handleScroll}>
          {
            usersList?.length !== 0 && !userToProcessesError && !userToProcessesLoading &&
            usersList?.map((userToProcess, index) => {
              return (
                (currentStage(userToProcess.nextUpdate).key === selectedState.key || selectedState.key === "all") &&
                <div className="follow-up__item" 
                  key={userToProcess.id}
                  onClick={() => 
                    selectFollowUpPatient(userToProcess)
                  }
                >
                  <FollowUpBox
                    key={userToProcess.id}
                    userToProcess={userToProcess}
                    currentStage={currentStage(userToProcess.nextUpdate)}
                  />
                </div>
              )
            })
          }
          {
          usersList?.length === 0 && !userToProcessesLoading &&
              <ZaiaReaction
                zaiaImg={logoZaiaEmpty}
                text={t("globally.noResults")}
                subText={t("globally.noMatchingPatients")}
                sizeText="33.25px"
                sizeSubText="15px"
                textColor="var(--blue-color)"
                widthimg="210px"
              />
          }
          {
            userToProcessesError && !userToProcessesLoading &&
              <ZaiaReaction
                zaiaImg={logoZaiaAlert}
                sizeText="20px"
                text={t("globally.genericError")}
                textColor="var(--blue-color)"
                widthimg="150px"
              />
          }
          {
            userToProcessesLoading &&
              <ZaiaReaction
                zaiaImg={logoZaiaLoading}
                sizeText="20px"
                textColor="var(--blue-color)"
                widthimg="150px"
              />
          }
          {isFetchingMore && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
              <ZaiaReaction widthimg="40px" zaiaImg={logoZaiaLoading}/>
            </div>
          )}         

        </div>

        <div className="follow-up-list__bottom">
          {/* <div className="pagination">
            <i className="pagination__arrow pagination__arrow--back icon zaia-icono-atras"></i>
            <div className="pagination__number pagination__number--selected">1</div>
            <div className="pagination__number">2</div>
            <div className="pagination__number">3</div>
            <div className="pagination__number">4</div>
            <div className="pagination__number">5</div>
            <i className="pagination__arrow pagination__arrow--next icon zaia-i-next"></i>
          </div> */}

          {/* <div className="add-patient">
            <div className="add-patient__text">Add patient</div>
            <i className="add-patient__icon icon zaia-i-invitepatient"></i>
          </div> */}
        </div>
      </div>
    </S.FollowUpView>
  );
}

export default FollowUpView;
